import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  InputAdornment,
  IconButton,
  Alert,
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff,
  Lock as LockIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../contexts/AdminContext';
import { useTheme } from '../contexts/ThemeContext';

// This component is not linked from anywhere in the main UI
// It can only be accessed by directly navigating to the URL
const SecretAdminLogin: React.FC = () => {
  const { loginAdmin } = useAdmin();
  const { mode } = useTheme();
  const navigate = useNavigate();
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Determine colors based on current theme
  const isLight = mode === 'light';
  const textColor = isLight ? '#000000' : '#FFFFFF';
  const paperBg = isLight ? '#FFFFFF' : 'rgba(17, 24, 39, 0.8)';
  const borderColor = isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255, 255, 255, 0.1)';

  const handleTogglePassword = () => {
    setShowPassword(prev => !prev);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!username || !password) {
      setError('Please enter both username and password.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const success = await loginAdmin(username, password);
      
      if (success) {
        // Redirect to admin dashboard on success
        navigate('/management-portal/dashboard');
      } else {
        setError('Invalid username or password.');
      }
    } catch (error) {
      setError('An error occurred during login. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        background: isLight 
          ? 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)'
          : 'linear-gradient(135deg, #111827 0%, #0f172a 100%)',
        py: 4
      }}
    >
      <Container maxWidth="xs">
        <Paper 
          elevation={24} 
          sx={{ 
            p: 4, 
            borderRadius: 2,
            backdropFilter: 'blur(10px)',
            backgroundColor: paperBg,
            border: `1px solid ${borderColor}`,
          }}
        >
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box 
              sx={{ 
                width: 60,
                height: 60,
                borderRadius: '50%',
                backgroundColor: 'primary.main',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 2
              }}
            >
              <LockIcon sx={{ fontSize: 32, color: 'common.white' }} />
            </Box>
            
            <Typography variant="h5" component="h1" sx={{ mb: 1, fontWeight: 600, color: textColor }}>
              Admin Access
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Secure management portal
            </Typography>
            
            {error && (
              <Alert 
                severity="error" 
                sx={{ width: '100%', mb: 3 }}
              >
                {error}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Username"
                name="username"
                autoComplete="off"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="off"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 4 }}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ 
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 500,
                  borderRadius: 1,
                }}
              >
                {loading ? 'Authenticating...' : 'Sign In'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default SecretAdminLogin; 